import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/testimonials", label: "Student Success" },
  { to: "/resources", label: "Resources" },
  { to: "/media", label: "Media" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const drawerRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const firstLinkRef = useRef(null);

  // Home hero is dark — navbar must be transparent/dark by default, then flip to
  // white when scrolled. Other pages use light theme immediately.
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // Focus trap + Esc handling + focus return
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstLinkRef.current?.focus(), 40);

    const handler = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleBtnRef.current?.focus();
        return;
      }
      if (e.key === "Tab" && drawerRef.current) {
        const focusables = drawerRef.current.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
      clearTimeout(t);
    };
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setTimeout(() => toggleBtnRef.current?.focus(), 40);
  };

  const isTransparent = onHome && !scrolled;
  const textColor = isTransparent ? "text-white" : "text-brand-navy";
  const linkColor = isTransparent ? "text-white/85 hover:text-brand-gold" : "text-brand-navy/85 hover:text-brand-royal";
  const activeColor = isTransparent ? "text-brand-gold" : "text-brand-royal";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isTransparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-lg shadow-[0_1px_0_0_rgba(10,30,110,0.08)]"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-3 md:px-10 md:py-4">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3 min-h-[48px]">
          <span className={`grid h-11 w-11 place-items-center rounded-lg font-display text-lg font-bold ${
            isTransparent ? "bg-brand-cta text-brand-navy" : "bg-brand-navy text-brand-gold"
          }`}>
            KB
          </span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className={`font-display text-base font-bold tracking-wide ${textColor}`}>Kuldeep Batra</span>
            <span className={`mt-1 text-[10px] uppercase tracking-[0.25em] ${isTransparent ? "text-brand-gold" : "text-brand-royal"}`}>
              Educator · Mentor · Speaker
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`relative inline-flex min-h-[44px] items-center text-sm font-medium tracking-wide transition-colors ${
                pathname === l.to ? activeColor : linkColor
              }`}
              aria-current={pathname === l.to ? "page" : undefined}
            >
              {l.label}
              {pathname === l.to && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-1 left-0 h-0.5 w-full rounded-full bg-brand-cta"
                />
              )}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="group hidden min-h-[48px] items-center gap-2 rounded-lg bg-brand-cta px-5 py-2.5 text-sm font-semibold text-brand-navy shadow-[0_4px_18px_-4px_rgba(255,215,0,0.5)] transition-all hover:scale-[1.02] hover:shadow-[0_6px_22px_-4px_rgba(255,215,0,0.65)] md:inline-flex"
          >
            Book a Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            ref={toggleBtnRef}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`grid h-12 w-12 place-items-center rounded-lg border lg:hidden ${
              isTransparent ? "border-white/25 text-white" : "border-brand-navy/15 text-brand-navy"
            }`}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            id="mobile-menu"
            ref={drawerRef}
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-brand-navy/10 bg-white shadow-lg lg:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l, i) => (
                <Link
                  key={l.to}
                  to={l.to}
                  ref={i === 0 ? firstLinkRef : null}
                  onClick={closeMenu}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex min-h-[56px] items-center justify-between border-b border-brand-navy/10 py-4 font-display text-lg font-semibold text-brand-navy"
                >
                  {l.label}
                  <ArrowUpRight className="h-4 w-4 text-brand-royal" />
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={closeMenu}
                data-testid="mobile-book-consultation"
                className="mt-4 flex min-h-[52px] items-center justify-center gap-2 rounded-lg bg-brand-cta py-3 text-sm font-semibold text-brand-navy"
              >
                Book a Consultation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
